import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, AlertCircle, Loader2, ArrowRight, Sparkles, User, ShieldCheck, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminLoginPremium = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const totpInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username, 
          password,
          totp_code: requires2FA ? totpCode : null 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Check if 2FA is required
        if (data.requires_2fa) {
          setRequires2FA(true);
          setTimeout(() => totpInputRef.current?.focus(), 100);
          setLoading(false);
          return;
        }
        
        localStorage.setItem('viva_admin_token', data.access_token);
        localStorage.setItem('viva_admin_expires', Date.now() + data.expires_in * 1000);
        
        // Pass must_change_password flag
        onLoginSuccess({
          ...data,
          must_change_password: data.must_change_password || false
        });
      } else {
        // Handle blocked IP with countdown
        if (response.status === 403 && data.detail?.includes('minutos')) {
          const match = data.detail.match(/(\d+) minutos/);
          if (match) {
            const minutes = parseInt(match[1]);
            setCountdown(minutes * 60);
            const interval = setInterval(() => {
              setCountdown(prev => {
                if (prev <= 1) {
                  clearInterval(interval);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          }
        }
        
        setError(data.detail || 'Credenciais inválidas');
        setShake(true);
        setTimeout(() => setShake(false), 500);
        
        // Clear 2FA code on error
        if (requires2FA) {
          setTotpCode('');
        }
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleBackTo = () => {
    setRequires2FA(false);
    setTotpCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#050B14] flex items-center justify-center relative overflow-hidden">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs */}
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#22c55e]/20 rounded-full blur-[100px]"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#22c55e]/10 rounded-full blur-[80px]"
        />
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 4 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#22c55e]/5 rounded-full blur-[120px]"
        />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(34,197,94,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(34,197,94,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`relative z-10 w-full max-w-md mx-4 ${shake ? 'animate-shake' : ''}`}
      >
        <div className="bg-white/[0.02] backdrop-blur-2xl border border-white/[0.08] rounded-2xl p-8 shadow-2xl">
          {/* Logo & Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-center mb-8"
          >
            <motion.div 
              whileHover={{ scale: 1.05, rotate: 5 }}
              className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#22c55e] to-[#16a34a] p-0.5 shadow-[0_0_40px_rgba(34,197,94,0.4)]"
            >
              <div className="w-full h-full rounded-2xl bg-[#050B14] flex items-center justify-center">
                {requires2FA ? (
                  <ShieldCheck className="w-10 h-10 text-[#22c55e]" />
                ) : (
                  <Sparkles className="w-10 h-10 text-[#22c55e]" />
                )}
              </div>
            </motion.div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {requires2FA ? 'Verificação 2FA' : 'Viva Impact'}
            </h1>
            <p className="text-slate-400 text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {requires2FA ? 'Digite o código do autenticador' : 'Painel Administrativo'}
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <AnimatePresence mode="wait">
              {!requires2FA ? (
                <motion.div
                  key="credentials"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-5"
                >
                  {/* Username */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Usuário
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Digite seu usuário"
                        required
                        disabled={loading || countdown > 0}
                        className="w-full pl-12 pr-4 py-3.5 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-slate-500 
                                 focus:border-[#22c55e]/50 focus:ring-2 focus:ring-[#22c55e]/20 focus:outline-none
                                 disabled:opacity-50 disabled:cursor-not-allowed
                                 transition-all duration-300"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        data-testid="login-username"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Senha
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Digite sua senha"
                        required
                        disabled={loading || countdown > 0}
                        className="w-full pl-12 pr-12 py-3.5 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-slate-500 
                                 focus:border-[#22c55e]/50 focus:ring-2 focus:ring-[#22c55e]/20 focus:outline-none
                                 disabled:opacity-50 disabled:cursor-not-allowed
                                 transition-all duration-300"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="2fa"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  {/* 2FA Code */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Código de Autenticação
                    </label>
                    <div className="relative">
                      <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        ref={totpInputRef}
                        type="text"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                        placeholder="000000"
                        required
                        disabled={loading}
                        maxLength={8}
                        autoComplete="one-time-code"
                        className="w-full pl-12 pr-4 py-3.5 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-slate-500 
                                 focus:border-[#22c55e]/50 focus:ring-2 focus:ring-[#22c55e]/20 focus:outline-none
                                 text-center text-2xl tracking-[0.5em] font-mono
                                 transition-all duration-300"
                        style={{ fontFamily: 'monospace' }}
                        data-testid="login-2fa-code"
                      />
                    </div>
                    <p className="text-xs text-slate-500 text-center">
                      Digite o código de 6 dígitos do Google Authenticator ou um código de backup
                    </p>
                  </div>
                  
                  {/* Back button */}
                  <button
                    type="button"
                    onClick={handleBackTo}
                    className="text-sm text-[#22c55e] hover:text-[#4ade80] transition-colors"
                  >
                    ← Voltar para login
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Countdown Timer */}
            <AnimatePresence>
              {countdown > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-center gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl"
                >
                  <Lock className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Bloqueado por {formatCountdown(countdown)}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Message */}
            <AnimatePresence>
              {error && countdown === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span className="text-red-400 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={loading || countdown > 0}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-4 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl
                       flex items-center justify-center gap-2
                       hover:bg-[#4ade80] hover:shadow-[0_0_40px_rgba(34,197,94,0.5)]
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-300"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{requires2FA ? 'Verificando...' : 'Entrando...'}</span>
                </>
              ) : (
                <>
                  <span>{requires2FA ? 'Verificar' : 'Entrar'}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </motion.form>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="text-center text-slate-500 text-xs mt-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Acesso restrito a administradores
          </motion.p>
        </div>
      </motion.div>

      {/* Shake Animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-10px); }
          40% { transform: translateX(10px); }
          60% { transform: translateX(-10px); }
          80% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default AdminLoginPremium;
